import React, { MouseEvent, useState, Dispatch } from 'react';
import Select, { components } from 'react-select'
import { IPlayer, IGameStatus } from '../../interfaces';
import { fetchGameStatus } from '../../features/player/gameStatusSlice'
import { postHost } from '../../features/player/gameSlice'
import { useAppDispatch } from '../../app/hooks'
import Modal from 'react-modal';
import { ChevronDown as ChevronDownIcon} from '@carbon/icons-react'

interface HostProps {
    currentGameId: string;
    currentGameStatus: IGameStatus;
    disabled: boolean;
    setIsHosting: Dispatch<boolean>;
}

Modal.setAppElement('#root');

const Host: React.FC<HostProps> = ({ currentGameId, currentGameStatus, disabled, setIsHosting }) => {
    const customStyles = {
        content: {
          background: '#323544',
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          marginRight: '-50%',
          padding: '0',
          border: 'none',
          width: '480px',
          transform: 'translate(-50%, -50%)',
        }
      };

    const dispatch: any = useAppDispatch();

    const [newHostModalIsOpen, setNewHostModalIsOpen] = useState<boolean>(false);
    const [newHost, setNewHost] = useState<string>('');
    const [newHostLoading, setNewHostLoading] = useState<boolean>(false);
    const hostingEmail = '';
    const hostingError = '';
    /* NOTE: Leaving this here because we might need it again soon once we start enforcing hosting rules.
    const [hostingEmail, setHostingEmail] = useState<string>('');
    const [hostingError, setHostingError] = useState<boolean>(false);
    */

    const CustomDropdownIndicator = (props: any) => {
        return (
          <components.DropdownIndicator {...props}>
            <ChevronDownIcon color="#161616"/>
          </components.DropdownIndicator>
        );
      };

    const dropdownStyles = {
        option: (provided: any, { isFocused, isSelected } : { isFocused: any, isSelected: any }) => ({
            ...provided,
            backgroundColor: isFocused ? "#FFFFFF" : "#FFFFFF",
            fontSize: 14,
            color: "#161616",
            ":hover": {
              backgroundColor: "#878FA1",
              color: "#FFFFFF"
            }
        }),
        singleValue: (provided: any, state: any) => ({
            ...provided,
            padding: 0,
            color: '#161616',
            backgroundColor: "#FFFFFF",
            width: 250,
            fontSize: 14,
            marginLeft: 8
        }),
        container:  (provided: any, state: any) => ({
            ...provided,
            border: "none",
            width: 250,
            height: 50,
            borderBottom: "1px solid #161616"
        }),
        control:  (provided: any, state: any) => ({
            ...provided,
            backgroundColor: "#FFFFFF",
            color: "#161616",
            borderRadius: 0,
            border: "none",
            boxShadow: 0,
            width: 250,
            height: 49
        }),
        placeholder:  (provided: any, state: any) => ({
            ...provided,
            color: "#161616",
            fontSize: 14
        }),
        indicatorSeparator:  (provided: any, state: any) => ({
            ...provided,
            display: "none"
        }),
        menu:  (provided: any, state: any) => ({
            ...provided,
            width: 250,
            color: '#161616',
            backgroundColor: "#FFFFFF",
            top: 50,
            zIndex: 3000
        }),
        input:  (provided: any, state: any) => ({
            ...provided,
            marginTop: 5,
            height: 37
        }),
        menuList:  (provided: any, state: any) => ({
            ...provided,
            color: '#161616',
            backgroundColor: "#FFFFFF",
            paddingTop: 0,
            paddingBottom: 0,
            zIndex: 3000
        }),
        indicatorContainer:  (provided: any, state: any) => ({
            ...provided,
            color: '#161616'
        })
    }

    const options: any[] = [];

    currentGameStatus.players.forEach((player: IPlayer) => {
        if (currentGameStatus.host !== player.userID) {
            options.push({ value: player.userID, label: player.userID })
        }
    })

    const openNewHostModal = () => {
        setNewHostModalIsOpen(true);
    }
    
    const closeNewHostModal = () => {
        setNewHostModalIsOpen(false);
    }

    const sendHostChange = async (ev: MouseEvent) => {

        if (newHostLoading) {
            return;
        }

        setNewHostLoading(true)

        const target = ev.currentTarget as HTMLElement;

        if (target) {
            await dispatch(postHost({gameId: currentGameId, hostId: newHost }))

            setIsHosting(false)
            dispatch(fetchGameStatus(currentGameId))
            closeNewHostModal()
            setNewHostLoading(false)
        }
    }

    const selectNewHost = async (selectedOption: any) => {
        openNewHostModal();
        setNewHost(selectedOption.value);

        /* NOTE: Leaving this here because we might need it again soon once we start enforcing hosting rules.
        let hostResponse = await dispatch(isHosting(selectedOption.value))
        if (hostResponse.payload === false) {
            setHostingError(false)
            setHostingEmail('')
            setNewHost(selectedOption.value)
            openNewHostModal()
        } else {
            setHostingError(true)
            setHostingEmail(selectedOption.value)
        }
        */
    }

    return ( <>
                { disabled ? <div className="host-select-value">{currentGameStatus.host}</div> : 
                    <Select
                        value={{label: currentGameStatus.host, value: currentGameStatus.host}}
                        onChange={selectNewHost}
                        components={{ DropdownIndicator: CustomDropdownIndicator }}
                        options={options}
                        styles={dropdownStyles}
                        className={'host-select'}
                        data-id={`host-${currentGameStatus.host}`}
                    /> 
                }
            <div className={`hosting-error ${ hostingError ? 'show-inline' : '' }`}><b>{hostingEmail}</b> is already hosting a game.</div>
            <Modal
                isOpen={newHostModalIsOpen}
                onRequestClose={closeNewHostModal}
                style={customStyles}
                contentLabel="Kick Player Modal"
            >
                <div className="new-modal-header">Switch Host? <img src="/img/light-grey-x.svg" className="new-modal-padding-close" alt="x" onClick={closeNewHostModal}></img></div>
                
                <div className="new-modal-text">Are you sure you want to make <span className="modal-scenario-name">{newHost}</span> the new host of the game?  You will no longer be able to access the game as its host.</div>

                <button onClick={closeNewHostModal} className="new-cancel-btn"><span>Cancel</span></button>
                <button onClick={sendHostChange} className={`new-confirm-btn ${newHostLoading ? 'start-loading' : ''}`}>
                    {newHostLoading ? <div className="game-save-loader-25"></div> : <></> }
                    <span>Confirm</span>
                </button>
            </Modal>
        </>
    )

}

export default Host;